import { memo } from 'react';
import { File, FileText, Image, FileSpreadsheet, Download, Eye, FolderOpen } from 'lucide-react';
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from '@/modules/shared/components/ui/AppCard';
import { Button } from '@/modules/shared/components/ui/button';
import { StatusBadge } from '@/modules/shared/components/ui/StatusBadge';
import { formatDisplayDate } from '../../../utils/formatUtils';
import type { Patient360DocumentDto } from '../../../types/patient360';
import { DocumentAiPanel } from '../DocumentAiPanel';

interface DocumentsTabProps {
  documents: Patient360DocumentDto[];
  canDownload?: boolean;
  onDownload?: (document: Patient360DocumentDto) => void;
  onView?: (document: Patient360DocumentDto) => void;
  /** When provided, renders the Document AI Processing panel below the listing. */
  programId?: string;
  /** Required alongside programId to scope AI extractions to this patient. */
  patientId?: string;
}

function getDocumentIcon(fileName: string) {
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return <Image className="h-5 w-5 text-info" />;
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return <FileSpreadsheet className="h-5 w-5 text-success" />;
  }
  if (['pdf'].includes(ext)) {
    return <FileText className="h-5 w-5 text-destructive" />;
  }
  return <File className="h-5 w-5 text-muted-foreground" />;
}

function DocumentCard({ 
  document, 
  canDownload, 
  onDownload, 
  onView 
}: { 
  document: Patient360DocumentDto;
  canDownload?: boolean;
  onDownload?: (document: Patient360DocumentDto) => void;
  onView?: (document: Patient360DocumentDto) => void;
}) {
  return (
    <div className="workspace-card rounded-xl p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          {getDocumentIcon(document.fileName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{document.fileName}</p>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={document.sourceType || 'Document'} variant="muted" />
          </div>
          {document.uploadedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Uploaded: {formatDisplayDate(document.uploadedAt)}
            </p>
          )}
        </div>
      </div>

      {(canDownload || onView) && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border-hairline">
          {onView && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(document)}
              className="h-7 text-xs flex-1"
            >
              <Eye className="h-3 w-3 mr-1" />
              View
            </Button>
          )}
          {canDownload && onDownload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDownload(document)}
              className="h-7 text-xs flex-1"
            >
              <Download className="h-3 w-3 mr-1" />
              Download
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export const DocumentsTab = memo(function DocumentsTab({
  documents,
  canDownload = true,
  onDownload,
  onView,
  programId,
  patientId,
}: DocumentsTabProps) {
  // Group documents by source type
  const groupedDocs = documents.reduce((acc, doc) => {
    const type = doc.sourceType || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {} as Record<string, Patient360DocumentDto[]>);

  if (documents.length === 0) {
    return (
      <div className="p-4 space-y-4">
        <AppCard className="rounded-xl">
          <AppCardContent className="p-4 text-center">
            <FolderOpen className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-sm font-semibold mb-1">No Documents</h3>
            <p className="text-sm text-muted-foreground">
              Patient documents will appear here once uploaded.
            </p>
          </AppCardContent>
        </AppCard>
        {programId && patientId && (
          <DocumentAiPanel programId={programId} patientId={patientId} />
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-2 flex-wrap">
        <StatusBadge status="Open" label={`${documents.length} Documents`} variant="info" />
        {Object.keys(groupedDocs).map(type => (
          <StatusBadge key={type} status={type} label={`${type}: ${groupedDocs[type].length}`} variant="muted" />
        ))}
      </div>

      {/* Documents by type */}
      {Object.entries(groupedDocs).map(([type, docs]) => (
        <AppCard key={type} className="rounded-xl">
          <AppCardHeader>
            <AppCardTitle className="text-sm">{type}</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {docs.map((doc, index) => (
                <DocumentCard
                  key={`${doc.sourceId}-${index}`}
                  document={doc}
                  canDownload={canDownload}
                  onDownload={onDownload}
                  onView={onView}
                />
              ))}
            </div>
          </AppCardContent>
        </AppCard>
      ))}

      {/* AI Processing — OCR + classification + structured field extraction */}
      {programId && patientId && (
        <DocumentAiPanel programId={programId} patientId={patientId} />
      )}
    </div>
  );
});
